import { apiClient, apiClientPaginated, ApiError } from '@/lib/api-client';
import type {
  Booking,
  BookingListItem,
  BookingListParams,
  BookingListAllParams,
  BookingListResult,
  BookingStats,
  BookingMutationResult,
  BookingPaymentPayload,
  CancelBookingResult,
  CreateBookingPayload,
  CreateBookingResult,
  RemindersResult,
  SendReminderPayload,
  UpdateBookingPayload,
} from './types';

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return `?${entries
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&')}`;
}

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) {
      return fallback;
    }
    throw err;
  }
}

const EMPTY_LIST: BookingListResult = { data: [], total: 0 };
const EMPTY_STATS: BookingStats = { total_count: 0, upcoming_count: 0, balance_sum: 0, cancelled_count: 0 };
const EMPTY_REMINDERS: RemindersResult = { reminders: [] };

export const bookingsApi = {
  list: (params: BookingListParams) => {
    const { festival_id, ...rest } = params;
    // The endpoint returns a paginated envelope { data: [...], meta: { total } }.
    // Use apiClientPaginated and flatten meta.total → BookingListResult.total.
    return withFallback(
      async () => {
        const res = await apiClientPaginated<BookingListItem[]>(
          `/festivals/${festival_id}/bookings${buildQuery({
            page: rest.page,
            limit: rest.limit,
            status: rest.status || undefined,
            delivery_date: rest.delivery_date,
            location_id: rest.location_id,
            search: rest.search,
          })}`,
        );
        const meta = res.meta as { total?: number } | undefined;
        return { data: res.data ?? [], total: meta?.total ?? res.data?.length ?? 0 };
      },
      EMPTY_LIST,
    );
  },

  // Cross-festival list — a single paginated, server-filtered query powering the
  // "All festivals" view (GitHub issue #296). Replaces the previous per-festival
  // fan-out + client-side merge/filter/paginate. festival_id is optional.
  listAll: (params: BookingListAllParams) => {
    return withFallback(
      async () => {
        const res = await apiClientPaginated<BookingListItem[]>(
          `/bookings${buildQuery({
            festival_id: params.festival_id,
            page: params.page,
            limit: params.limit,
            status: params.status || undefined,
            delivery_date: params.delivery_date,
            location_id: params.location_id,
            search: params.search,
          })}`,
        );
        const meta = res.meta as { total?: number } | undefined;
        return { data: res.data ?? [], total: meta?.total ?? res.data?.length ?? 0 };
      },
      EMPTY_LIST,
    );
  },

  // Cross-festival KPI aggregate — same filters as listAll(), never paginated,
  // so the "All festivals" KPI cards reflect true server-side totals (#296).
  statsAll: (params: BookingListAllParams) =>
    withFallback(
      () =>
        apiClient<BookingStats>(
          `/bookings/stats${buildQuery({
            festival_id: params.festival_id,
            status: params.status || undefined,
            delivery_date: params.delivery_date,
            location_id: params.location_id,
            search: params.search,
          })}`,
        ),
      EMPTY_STATS,
    ),

  // Festival-wide KPI aggregate — same filters as list(), but never paginated,
  // so KPI cards stay constant across pages (GitHub issue #289).
  stats: (params: BookingListParams) => {
    const { festival_id, ...rest } = params;
    return withFallback(
      () =>
        apiClient<BookingStats>(
          `/festivals/${festival_id}/bookings/stats${buildQuery({
            status: rest.status || undefined,
            delivery_date: rest.delivery_date,
            location_id: rest.location_id,
            search: rest.search,
          })}`,
        ),
      EMPTY_STATS,
    );
  },

  create: (festivalId: string, payload: CreateBookingPayload) =>
    apiClient<CreateBookingResult>(`/festivals/${festivalId}/bookings`, {
      method: 'POST',
      body: payload,
    }),

  getById: (bookingId: string) =>
    apiClient<Booking>(`/bookings/${bookingId}`),

  reminders: (bookingId: string) =>
    withFallback(() => apiClient<RemindersResult>(`/bookings/${bookingId}/reminders`), EMPTY_REMINDERS),

  update: (bookingId: string, payload: UpdateBookingPayload) =>
    apiClient<Booking>(`/bookings/${bookingId}`, { method: 'PUT', body: payload }),

  cancel: (bookingId: string) =>
    apiClient<CancelBookingResult>(`/bookings/${bookingId}`, { method: 'DELETE' }),

  addDeposit: (bookingId: string, payload: BookingPaymentPayload) =>
    apiClient<BookingMutationResult>(`/bookings/${bookingId}/deposit`, {
      method: 'POST',
      body: payload,
    }),

  payBalance: (bookingId: string, payload: BookingPaymentPayload) =>
    apiClient<BookingMutationResult>(`/bookings/${bookingId}/balance`, {
      method: 'POST',
      body: payload,
    }),

  sendReminder: (bookingId: string, payload: SendReminderPayload) =>
    apiClient<{ booking_id: string; reminder_sent: boolean; channel: string; sent_at: string }>(`/bookings/${bookingId}/reminder`, {
      method: 'POST',
      body: payload,
    }),
};
